import { useRef, useContext, useEffect } from 'react' 

import { Check, X as XIcon, Minus } from "lucide-react"

import { TelemetryContext } from '../context/TelemetryContext'
import { useRegion } from '../hooks/useRegion' 

import { TEST_EVENTS, REGIONS } from '../constants/telemetryEvents'

import '../css/workspace.scss'
import { StudentTestCell } from '../../session/TestCaseIO'

// status (TerminalContext's testOutcomes): 'not_run' when the run stopped before the tests -
// a syntax error or an error at the top of the code. Students see those tests failed, as they
// always have (the research lead, 2026-11-02), with the error in the terminal; the run's records
// keep not_run.
const PassIcon = ({ result, status }) => {
  if (result === null) return <div className="ws-test-icon ws-test-icon-pending" title="Not run yet"><Minus className="ws-icon-md" /></div>
  if (result) return <div className="ws-test-icon ws-test-icon-passed" title="Passed" aria-label="Passed"><Check className="ws-icon-md" /></div>
  const title = status === 'not_run' ? 'Failed: the code stopped with an error before the tests ran. See the terminal.' : 'Failed'
  return <div className="ws-test-icon ws-test-icon-failed" title={title} aria-label="Failed"><XIcon className="ws-icon-md" /></div>
}

export default function TestCases({ tests }) {
  const { logTelemetryEvent } = useContext(TelemetryContext)
  
  const containerRef = useRef(null)

  useRegion(REGIONS.TEST_CASES, containerRef)

  // Track selection and copy events
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    let lastSelection = ''

    const handleSelectionChange = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText && selectedText !== lastSelection) {
        lastSelection = selectedText
        logTelemetryEvent(TEST_EVENTS.SELECT, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      } else if (!selectedText) {
        lastSelection = ''
      }
    }

    const handleCopy = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText) {
        logTelemetryEvent(TEST_EVENTS.COPY, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      }
    }

    el.addEventListener('mouseup', handleSelectionChange)
    el.addEventListener('keyup', handleSelectionChange)
    el.addEventListener('copy', handleCopy)

    return () => {
      el.removeEventListener('mouseup', handleSelectionChange)
      el.removeEventListener('keyup', handleSelectionChange)
      el.removeEventListener('copy', handleCopy)
    }
  }, [logTelemetryEvent])

  return (
    <div ref={containerRef} className="ws-content-section">
      <h3 className="ws-section-title">Test Cases</h3>
      <div className="ws-test-table-wrapper">
        <table className="ws-test-table">
          <thead>
            <tr>
              <th>Name</th>
              <th className="ws-result-column">Result</th>
            </tr>
          </thead>
          <tbody>
            {tests.map((test, index) => (
              <tr key={index}>
                {/* test_list rows carry `view` (testDataFormatter.js): public ones show their
                    input and expected output. Web-dev checks are names only. */}
                <td>
                  {test.view ? <StudentTestCell view={test.view} /> : test.testName}
                  {/* A failed visible test: what its assert said, or the error it raised. */}
                  {test.message && <div className="ws-test-message">{test.message}</div>}
                </td>
                <td className="ws-result-column">
                  <PassIcon result={test.result} status={test.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}